"use client"

import { useEffect, useState } from "react"
import internal from "@/service/internal"
import { getDate } from "@/utils/dayjs"
import { Button, TextField, Typography } from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import { useSnackbar } from "notistack"
import posthog from "posthog-js"
import { useRouter } from "next/navigation"
import paths from "@/paths"
import localStorage from "@/utils/localStorage"
import LoadingPage from "@/components/ui/LoadingPage"

type ReportPayload = {
  errorMessage: string
  stack?: string
  name: string
  email: string
}

const errorPageInputSx = {
  maxWidth: 400,
  "& .MuiOutlinedInput-root": { backgroundColor: "rgba(255,255,255,0.1)" },
  "& .MuiOutlinedInput-root:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
  "& .MuiOutlinedInput-root:active": { backgroundColor: "rgba(255,255,255,0.15)" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.6)" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3)" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.6)" },
  "& .MuiOutlinedInput-input": { color: "white" },
  "& .MuiOutlinedInput-input::placeholder": { opacity: 0.7 },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" }
}

const Error = (props: { error: any; reset: () => void }) => {
  const { enqueueSnackbar } = useSnackbar()
  const router = useRouter()

  const [name, setName] = useState("")
  const [email, setEmail] = useState("")

  const errorMessage = props.error?.message || JSON.stringify(props.error)

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: ReportPayload) =>
      internal.post("/api/logs", {
        tipo: "ERROR",
        fecha: getDate().format().toString(),
        mensaje: {
          errorMessage: payload.errorMessage,
          stack: payload.stack,
          reportedBy: { name: payload.name, email: payload.email }
        }
      }),
    onSuccess: () => {
      enqueueSnackbar("Reporte de error enviado", { variant: "success" })
      try {
        localStorage.clear()
      } catch (error) {
        console.error("[Error] localStorage.clear failed:", error)
      }
    }
  })

  const sendErrorReport = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (process.env.NODE_ENV === "production") {
      try {
        posthog.captureException(props.error)
      } catch (err) {
        console.warn("[Error] posthog.captureException failed:", err)
      }
    }
    mutate({
      errorMessage: JSON.stringify(props?.error?.message || props?.error),
      stack: JSON.stringify(props.error?.stack),
      name: name.trim(),
      email: email.trim()
    })
  }

  useEffect(() => {
    try {
      if (errorMessage?.toLowerCase().includes("unauthorized")) {
        const currentPath = window.location.pathname

        const companySlug = currentPath.split("/")[1]

        if (companySlug && currentPath.includes("/admin")) {
          router.push(paths.admin(companySlug).login)
        } else {
          router.push(paths.login)
        }
      }
    } catch (error) {
      console.error("[Error] errorMessage failed:", error)
    }
  }, [errorMessage])

  if (errorMessage?.toLowerCase().includes("unauthorized")) {
    return <LoadingPage />
  }

  return (
    <form
      onSubmit={sendErrorReport}
      style={{
        display: "flex",
        justifyContent: "end",
        alignItems: "center",
        height: "100%",
        backgroundImage: "url('/pexels-andrew-2312369.jpg')",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "bottom",
        backgroundSize: "cover"
      }}
    >
      <div className="bg-black opacity-90 h-full w-full w-full sm:w-[50%] sm:mr-5 p-10 flex flex-col gap-8 text-white">
        <Typography variant="h1" className="">
          ¡Ups!
        </Typography>

        <Typography variant="h5">Parece que hubo un problema.</Typography>

        <TextField
          label="Nombre"
          value={name}
          onChange={(e) => setName(e.target.value)}
          variant="outlined"
          fullWidth
          sx={errorPageInputSx}
        />
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          variant="outlined"
          fullWidth
          sx={errorPageInputSx}
        />

        <Button type="submit" variant="contained" color="info" className="w-fit" disabled={isPending}>
          {isPending ? "Enviando..." : "Enviar reporte de error"}
        </Button>
      </div>
    </form>
  )
}

export default Error
